import * as readline from 'readline';
import { BaserowClient } from '../../client/baserow-client';
import { UserOperations } from '../../client/user-operations';
import { ConfigManager } from '../config/manager';
import { configSet } from './config';
import * as fs from 'fs';
import * as path from 'path';
import * as os from 'os';

// Define the error state file path (must match the one in client.ts)
const ERROR_STATE_FILE = path.join(os.homedir(), '.baserow_error_state');

/**
 * Clear error state if it exists
 */
function clearErrorState(): void {
  try {
    if (fs.existsSync(ERROR_STATE_FILE)) {
      fs.unlinkSync(ERROR_STATE_FILE);
    }
  } catch (err) {
    // Silently fail if we can't delete the error state
  }
}

/**
 * Creates a readline interface for handling user input
 */
function createReadlineInterface() {
  return readline.createInterface({
    input: process.stdin,
    output: process.stdout
  });
}

/**
 * Prompts the user for input with the given question
 */
function prompt(rl: readline.Interface, question: string): Promise<string> {
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      resolve(answer);
    });
  });
}

/**
 * Prompts the user for a password with masked input
 */
function promptPassword(rl: readline.Interface, question: string): Promise<string> {
  return new Promise((resolve) => {
    // First output the prompt manually
    process.stdout.write(question);
    
    // Configure stdin to disable automatic printing of user input
    const stdin = process.stdin;
    const isRaw = stdin.isRaw;
    if (!isRaw) {
      stdin.setRawMode && stdin.setRawMode(true);
    }
    
    let password = '';
    
    // Handle keypress events
    const onKeypress = (char: any, key: any) => {
      // Ctrl+C or Ctrl+D
      if ((key && key.ctrl && key.name === 'c') || 
          (key && key.ctrl && key.name === 'd') || 
          (key && key.name === 'return')) {
        stdin.removeListener('data', onData);
        stdin.setRawMode && stdin.setRawMode(isRaw);
        process.stdout.write('\n');
        resolve(password);
      } else if (key && key.name === 'backspace') {
        if (password.length > 0) {
          password = password.slice(0, -1);
          process.stdout.write('\b \b'); // Erase last character
        }
      } else if (char) {
        password += char;
        process.stdout.write('*'); // Show asterisk for each character
      }
    };
    
    // Data event handler
    const onData = (data: Buffer) => {
      const char = data.toString();
      // For each character in the buffer
      for (let i = 0; i < char.length; i++) {
        onKeypress(char[i], {
          name: char[i] === '\r' || char[i] === '\n' ? 'return' : 
                char[i] === '\u0008' || char[i] === '\u007F' ? 'backspace' : 'other'
        });
      }
    };
    
    stdin.on('data', onData);
  });
}

/**
 * Handles user login to Baserow.
 * @param options Command options including local flag and optional URL
 * @param credentials Optionally provide credentials for programmatic use
 * @returns An object with user info and config location
 * @throws If authentication fails or required info is missing
 */
export async function login(
  options?: { local?: boolean; url?: string },
  credentials?: { email: string; password: string }
): Promise<{
  user: { username: string; first_name: string };
  configLocation: string;
  url: string;
}> {
  clearErrorState();
  const config = ConfigManager.getInstance();
  const resolvedConfig = config.resolveConfig({});
  const useGlobalConfig = !options?.local;
  let url = options?.url || resolvedConfig.url;
  let email: string | undefined;
  let password: string | undefined;
  if (credentials) {
    email = credentials.email;
    password = credentials.password;
  }
  if (!url) {
    throw new Error('URL is required');
  }
  if (!email || !password) {
    throw new Error('Email and password are required');
  }
  const client = new BaserowClient({
    url,
  });
  const userOperations = new UserOperations(client);
  const authResponse = await userOperations.login(email, password);
  const authConfigOptions = { global: useGlobalConfig };
  const urlConfigOptions = { global: !options?.local };
  await configSet('token', authResponse.access_token, authConfigOptions);
  await configSet('refreshToken', authResponse.refresh_token, authConfigOptions);
  await configSet('tokenType', 'JWT', authConfigOptions);
  await configSet('userEmail', authResponse.user.username, authConfigOptions);
  await configSet('userFirstName', authResponse.user.first_name, authConfigOptions);
  await configSet('url', url, urlConfigOptions);
  const configLocationMsg = useGlobalConfig
    ? 'Authentication credentials stored in global config (~/.baserowrc.jsonc)'
    : 'Authentication credentials stored in local project config (.baserowrc.jsonc)';
  return {
    user: {
      username: authResponse.user.username,
      first_name: authResponse.user.first_name
    },
    configLocation: configLocationMsg,
    url
  };
} 